import { Injectable } from '@angular/core';

import { MessageService } from 'primeng/api';
import { combineLatest, Observable, of, Subscription, merge, forkJoin, EMPTY, throwError } from 'rxjs';
import { filter, map, switchMap, take, tap, shareReplay, catchError, distinctUntilChanged } from 'rxjs/operators';


import { ViRoutingService } from './vi-routing.service';
import { CurrentResourceAbstractService } from '../../Common/Basic/service/current-resource-abstract.service';
import { ResourceAction } from '../../Common/Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../Common/Basic/enums/resource-actions.enum';
import { messageSeverityEnum } from '../../Common/Misc/enums/message-severity.enum';
import { MultiBaseAbstractApi } from '../../api/genericApi/multi-base-abstract.api';
import { AgentsApiService } from '../../api/agents/agents.api';
import { ViMonitorStatusEnum } from '../enums/vi-monitor-status.enum';
import { ViMonitorApi } from '../../api/visibilityIntelligence/models/vi-monitor.api.model';
import { ViMonitorListItemApi } from '../../api/visibilityIntelligence/models/vi-monitor-list-item.api.model';
import { ViQuestionApi } from '../../api/visibilityIntelligence/models/vi-question.api.model';

import {
    ViGenerateQuestionsRequestApi, ViGenerateQuestionsResponseApi, ViQuestionDimensionCountsApi
} from '../../api/visibilityIntelligence/models/vi-generate-questions.api.model';
import { ViMonitor } from '../models/vi-monitor.model';
import { ViMonitorListItem } from '../models/vi-monitor-list-item.model';
import { ViApiService } from '../../api/visibilityIntelligence/vi.api.service';
import { ViRouteModel } from '../models/vi-route.model';
import { ViRouteType } from '../enums/vi-route-type.enum';

import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../Core/Users/enums/user-permissions.enum';
import { sortByDate } from '../../Common/Misc/helpers/sort/sort.helper';
import { ActionsEnum } from '../../Common/Basic/enums/actions.enum';
import { DisplayGroupsService } from '../../Common/Layout/services/display-groups.service';
import { ConfirmDialogComponent } from '../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../Common/Dialogs/models/confirm-dialog.model';
import { AuditableModel } from '../../Common/Misc/models/auditable.model';
import { DisplayGroupModel } from '../../Common/Layout/models/display-group.model';


@Injectable()
export class ViMonitorService extends CurrentResourceAbstractService<ViMonitor, ViMonitorApi, ViMonitorListItem, ViMonitorListItemApi> {

    all$: Observable<ViMonitorListItem[]> = this.userStateService.hasAllPermissions(
        [
            userPermissionsEnum.visibilityIntelligence,
            userPermissionsEnum.openAI
        ]
    ).pipe(
        switchMap((hasPermission) =>
            hasPermission ? merge(
                this.fetchList$(),
                this.refreshAllManualSubject
            ) : of([])
        ),
        shareReplay(1)
    );

    /**
     * Always clone a baseline, even for new (id-less) monitors, so dirty checking reflects
     * actual user edits rather than treating every new monitor as dirty from the start.
     */
    currentResource$: Observable<ViMonitor> = this.currentResourceSubject.asObservable().pipe(
        tap(resource => {
            this.cachedResource = resource ? resource.clone() : null;
        }),
        shareReplay(1)
    );

    /**
     * Redeclare, since `currentResource$` has a new definition and must reference this class's version
     */
    resourceDirty$: Observable<boolean> = combineLatest([this.currentResource$, this.currentResourceModified$]).pipe(
        map(([resource, x]) => {
            if (!resource) {
                return false;
            }

            return !resource.equals(this.cachedResource);
        }),
        shareReplay(1)
    );


    totalQuestionsUsed: number = 0;

    /** Sums saved question counts across ACTIVE monitors only. */
    totalQuestionsUsed$: Observable<number> = this.all$.pipe(
        map(list => list.reduce((total, item) => total + (item.status === ViMonitorStatusEnum.active ? item.questionCount : 0), 0)),
        tap(total => this.totalQuestionsUsed = total),
        shareReplay(1)
    );

    subscriptions: Subscription[] = [];

    private monitorQuestionLimit$: Observable<number>;


    constructor(
        private viApiService: ViApiService,
        private agentsApiService: AgentsApiService,
        private messageService: MessageService,
        private viRoutingService: ViRoutingService,
        private userStateService: CurrentUserStateService,
        private displayGroupsService: DisplayGroupsService,
    ) {
        super();

        this.subscriptions.push(
            this.viRoutingService.getCurrentRoute().pipe(
                filter(route => !!route),
                distinctUntilChanged((a, b) => this.routeWorkKey(a) === this.routeWorkKey(b)),
                switchMap((route: ViRouteModel) => this.onUpdateRoute(route)),
            ).subscribe()
        );
    }

    protected fetchList$(): Observable<ViMonitorListItem[]> {
        return this.viApiService.getList().pipe(
            map(list => list
                .map(item => this.constructListModel(item))
                .sort(sortByDate)
            ),
        );
    }

    getApiService(): MultiBaseAbstractApi<ViMonitorApi, ViMonitorListItemApi, number> {
        return this.viApiService;
    }

    constructResourceModel(apiModel: ViMonitorApi): ViMonitor {
        return new ViMonitor(apiModel);
    }

    constructListModel(apiModel: ViMonitorListItemApi): ViMonitorListItem {
        return new ViMonitorListItem(apiModel);
    }

    create(monitor: ViMonitor): Observable<number> {
        return this.createResource(
            monitor,
            { actionType: ResourceActionTypes.CREATE, refreshResource: true }
        ).pipe(
            switchMap(id => this.refresh(id))
        );
    }


    update(monitor: ViMonitor): Observable<number> {
        return this.updateResource(
            monitor,
            { actionType: ResourceActionTypes.UPDATE, refreshResource: true }
        ).pipe(
            switchMap(id => this.refresh(id))
        );
    }

    delete(id: number): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((currentMonitor) => {
                return this.deleteResource(id, {
                    actionType: ResourceActionTypes.DELETE,
                    resetResource: currentMonitor?.id === id,
                }).pipe(
                    switchMap(deletedId => this.refresh(deletedId)),
                    tap(() => {
                        if (currentMonitor?.id === id) {
                            this.viRoutingService.gotoRoot();
                        }
                    })
                );
            }),
            switchMap(monitorId => this.refresh(monitorId))
        );
    }


    rename(id: number, name: string): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((currentMonitor) => {
                return this.getById(id).pipe(
                    switchMap((monitor) => {
                        monitor.name = name;
                        return this.updateResource(monitor, {
                            actionType: ResourceActionTypes.RENAME,
                            refreshResource: currentMonitor?.id === id,
                        });
                    }),
                    switchMap(monitorId => this.refresh(monitorId))
                );
            })
        );
    }

    updateVisibility(id: number, action: ActionsEnum): Observable<number> {
        return this.getById(id).pipe(
            switchMap((monitor) => {
                monitor.isHidden = action === ActionsEnum.hide;

                return this.updateResource(monitor, { actionType: ResourceActionTypes.UPDATE }, false);
            }),
            switchMap(monitorId => this.refresh(monitorId))
        );
    }

    updateFavourites(addIds: number[], removeIds: number[]): Observable<void> {
        return this.viApiService.updateFavorites(addIds, removeIds).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                throw new Error(error);
            }),
            switchMap(() => this.refresh(null)),
        );
    }


    updateGroup(monitorIds: number[], group: DisplayGroupModel, emitMessages: boolean = true): Observable<void> {
        let request: Observable<void>;

        if (group?.id) {
            request = this.viApiService.updateDisplayGroup(monitorIds, [], group.id);
        } else {
            request = this.viApiService.removeDisplayGroup(monitorIds);
        }

        return request.pipe(
            tap(() => {
                if (emitMessages) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform('Common.lhp.moveToGroup.success.detail.generic',
                            { name: group.name }
                        )
                    });
                }
            }),
            catchError(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                return EMPTY;
            }),
            switchMap(() => this.refresh(null)),
        );
    }

    activate(id: number): Observable<ViMonitor> {
        let activated: ViMonitor;

        return this.viApiService.activateMonitor(id).pipe(
            switchMap(() => this.getById(id)),
            tap((monitor) => {
                activated = monitor;
                this.actionCompletedSubject.next({
                    actionType: ResourceActionTypes.ACTIVATED,
                    resource: monitor,
                    refreshResource: true
                });
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    actionType: ResourceActionTypes.ACTIVATED,
                    resource: activated,
                    error: err,
                });
                return throwError(() => err);
            }),
            switchMap(monitor => this.refresh(monitor)),
        );
    }

    pause(id: number): Observable<ViMonitor> {
        let paused: ViMonitor;

        return this.viApiService.pauseMonitor(id).pipe(
            switchMap(() => this.getById(id)),
            tap((monitor) => {
                paused = monitor;
                this.actionCompletedSubject.next({
                    actionType: ResourceActionTypes.PAUSED,
                    resource: monitor,
                    refreshResource: true
                });
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    actionType: ResourceActionTypes.PAUSED,
                    resource: paused,
                    error: err,
                });
                return throwError(() => err);
            }),
            switchMap(monitor => this.refresh(monitor))
        );
    }

    bulkDelete(ids: number[]): Observable<number[]> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((currentMonitor) => {
                const action = {
                    actionType: ResourceActionTypes.BULK_DELETE,
                    resetResource: ids.includes(currentMonitor?.id),
                    count: ids.length
                };

                return this.bulkDeleteResourceByIds(ids, action).pipe(
                    tap(() => {
                        if (ids.includes(currentMonitor?.id)) {
                            this.viRoutingService.gotoRoot();
                        }
                    })
                );
            }),
            switchMap(x => this.refresh(x))
        );
    }

    confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(group => group.name);


        return new Observable<boolean>((subscriber) => {
            let isListSelected = true;
            let isSecondaryListSelected = false;

            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'deleteViMonitorsConfirm',
                    heading: 'VisibilityIntelligence.lhp.bulkDeleteConfirm.heading',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'VisibilityIntelligence.lhp.bulkDeleteConfirm.alertMessage',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'VisibilityIntelligence.lhp.bulkDeleteConfirm.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDelete(ids).subscribe();
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }

                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });
    }

    bulkUpdateVisibility(ids: number[], action: ActionsEnum): Observable<number[]> {
        return forkJoin(ids.map(id => this.getById(id))).pipe(
            switchMap((monitors: ViMonitor[]) => {
                monitors.forEach(sub => sub.isHidden = action === ActionsEnum.hide);
                return this.updateBulkResources(
                    monitors,
                    { actionType: ResourceActionTypes.BULK_VISIBILITY }
                );
            }),
            switchMap(monitorIds => this.refresh(monitorIds))
        );
    }

    generateQuestions(
        monitor: ViMonitor,
        questions: ViQuestionApi[] | undefined,
        dimensionCounts: ViQuestionDimensionCountsApi
    ): Observable<ViGenerateQuestionsResponseApi> {
        const monitorApi = monitor.toApiModel();
        const request: ViGenerateQuestionsRequestApi = {
            questions,
            brandInfo: monitorApi.brand ?? { primaryName: null, industry: null, companyOverview: null },
            competitors: monitorApi.competitors,
            targetKeywords: monitorApi.targetKeywords ?? [],
            language: monitorApi.language ?? undefined,
            dimensionCounts
        };

        return this.viApiService.generateQuestions(request).pipe(
            catchError(error => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'VisibilityIntelligence.querySetup.generate.error.heading',
                    detail: error?.error?.message ?? 'VisibilityIntelligence.querySetup.generate.error.message'
                });

                return EMPTY;
            })
        );
    }

    getMonitorQuestionLimit(): Observable<number> {
        this.monitorQuestionLimit$ ??= this.agentsApiService.getMonitorQuestionLimit().pipe(
            map(configuration => configuration.monitorQuestionLimit ?? 0),
            shareReplay(1)
        );
        return this.monitorQuestionLimit$;
    }

    getRemainingActiveQuestionQuota$(): Observable<number> {
        return combineLatest([
            this.getMonitorQuestionLimit(),
            this.totalQuestionsUsed$
        ]).pipe(
            map(([limit, used]) => limit - used)
        );
    }

    private refresh<T>(value: T): Observable<T> {
        return this.refreshAllManual().pipe(
            filter(x => !!x),
            take(1),
            map(() => value)
        );
    }

    messageCenter(actionResult: ResourceAction): void {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as ViMonitor;

        switch (actionType) {
            case ResourceActionTypes.CREATE:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.create.error.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.create.error.message', {
                            name: resource?.name ?? ''
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.create.success.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.create.success.message', {
                            name: resource?.name ?? ''
                        })
                    });
                }

                break;
            case ResourceActionTypes.UPDATE:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.update.error.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.update.error.message', {
                            name: resource?.name ?? ''
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.update.success.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.update.success.message', {
                            name: resource?.name ?? ''
                        })
                    });
                }

                break;
            case ResourceActionTypes.DELETE:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.delete.error.heading',
                        detail: 'VisibilityIntelligence.monitor.delete.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.delete.success.heading',
                        detail: 'VisibilityIntelligence.monitor.delete.success.message'
                    });
                }

                break;
            case ResourceActionTypes.RENAME:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.rename.error.heading',
                        detail: 'VisibilityIntelligence.monitor.rename.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.rename.success.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.rename.success.message', {
                            name: resource.name
                        })
                    });
                }
                break;
            case ResourceActionTypes.BULK_DELETE:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.bulkDelete.error.heading',
                        detail: 'VisibilityIntelligence.monitor.bulkDelete.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.bulkDelete.success.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.bulkDelete.success.message', {
                            number: actionResult.count
                        })
                    });
                }
                break;
            case ResourceActionTypes.BULK_VISIBILITY:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.bulkUpdate.error.heading',
                        detail: 'VisibilityIntelligence.monitor.bulkUpdate.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.bulkUpdate.success.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.bulkUpdate.success.message', {
                            number: actionResult.count
                        })
                    });
                }
                break;
            case ResourceActionTypes.ACTIVATED:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.activate.error.heading',
                        detail: 'VisibilityIntelligence.monitor.activate.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.activate.success.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.activate.success.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            case ResourceActionTypes.PAUSED:
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'VisibilityIntelligence.monitor.pause.error.heading',
                        detail: 'VisibilityIntelligence.monitor.pause.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'VisibilityIntelligence.monitor.pause.success.heading',
                        detail: this.localizationService.transform('VisibilityIntelligence.monitor.pause.success.message', {
                            name: resource.name
                        })
                    });
                }

                break;
        }
    }


    /**
     * Responds to changes in the route by updating VI
     * Sets a blank VI when the VI route is hit with no id
     */
    private onUpdateRoute(route: ViRouteModel): Observable<ViMonitor> {
        const id: number = +route.value;

        switch (route.type) {
            case ViRouteType.HYPE:
                this.setCurrentResource(null);
                return of(null);

            case ViRouteType.NEW:
                this.setCurrentResource(new ViMonitor());
                return of(null);

            case ViRouteType.EDIT:
            case ViRouteType.REPORTS:
            case ViRouteType.QUERIES_AND_ANSWERS:
                if (!id || isNaN(id)) {
                    this.setCurrentResource(null);
                    return of(null);
                }

                return this.getById(id).pipe(
                    tap(resource => this.setCurrentResource(resource))
                );

            default:
                this.setCurrentResource(null);
                return of(null);
        }
    }

    /**
     * Identity of the work this route requires. Every id-bearing route does the same
     * thing — fetch the monitor and set it current — so the id alone is the key.
     */
    private routeWorkKey(route: ViRouteModel): string {
        switch (route.type) {
            case ViRouteType.HYPE:
                return 'hype';
            case ViRouteType.NEW:
                return 'new';
            case ViRouteType.EDIT:
            case ViRouteType.REPORTS:
            case ViRouteType.QUERIES_AND_ANSWERS:
                return `monitor:${route.value}`;
            default:
                return 'unknown';
        }
    }
}
